import React, { useCallback, useEffect, useState, useRef } from 'react';
import { useParams, useLocation } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import {
	Box,
	Button,
	CircularProgress,
	Container,
	Grid,
	Typography,
	Paper,
	FormControlLabel,
	Switch,
	Dialog,
	DialogTitle,
	DialogContent,
	DialogActions,
	List,
	ListItem,
	ListItemText,
	FormControl,
	InputLabel,
	Select,
	MenuItem,
	OutlinedInput,
	Chip,
	Checkbox,
} from '@mui/material';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import WarningIcon from '@mui/icons-material/Warning';
import { useReactToPrint } from 'react-to-print';
import { loadSaleOrderWithAgencyContext } from 'pages/SalesOrder/SalesOrderScreen/helpers/saleOrderAgencyContext';
import { ISaleOrderDetail } from 'features/salesOrder/types';
import { CountryCodeEnum } from 'features/common/types';
import { RootState } from 'store';
import { OrderHeader } from './components/OrderHeader';
import { PassengerSummary } from './components/PassengerSummary';
import { groupServicesByPassenger, PassengerServicesData } from './helpers/groupServicesByPassenger';
import { getOrderVatTotals, isParaguayAgency } from './helpers/paraguayVatHelper';
import { countriesWithAgencyConfig } from 'features/auth/constants';

// Función helper para obtener la moneda local basada en el país
const getLocalCurrency = (country: CountryCodeEnum): string => {
	switch (country) {
		case CountryCodeEnum.COLOMBIA:
			return 'COP';
		case CountryCodeEnum.PARAGUAY:
		case CountryCodeEnum.TN_ITAU_PY:
			return 'PYG';
		case CountryCodeEnum.BOLIVIA:
			return 'BOB';
		case CountryCodeEnum.URUGUAY:
			return 'UYU';
		default:
			return 'USD';
	}
};

export const ResumeByPax = (): JSX.Element => {
	const { id } = useParams<{ id: string }>();
	const location = useLocation();
	const { auth } = useSelector((state: RootState) => state);
	const dispatch = useDispatch();

	const agencyIdFromQuery = new URLSearchParams(location.search).get('agencyId');
	const countryFromQuery = agencyIdFromQuery
		? countriesWithAgencyConfig.find((c) => c.agencyId === Number(agencyIdFromQuery))?.id
		: undefined;
	const [loading, setLoading] = useState<boolean>(true);
	const [salesOrderData, setSalesOrderData] = useState<ISaleOrderDetail | null>(null);
	const [useLocalCurrency, setUseLocalCurrency] = useState<boolean>(false);
	const [showReferencesAndTickets, setShowReferencesAndTickets] = useState<boolean>(true);
	const [showSummaryDetails, setShowSummaryDetails] = useState<boolean>(true);
	const [showCo2Emission, setShowCo2Emission] = useState<boolean>(true);
	const [warningDialogOpen, setWarningDialogOpen] = useState<boolean>(false);
	const [servicesWithoutLocalCurrency, setServicesWithoutLocalCurrency] = useState<string[]>([]);
	const [selectedPassengerIds, setSelectedPassengerIds] = useState<string[]>([]);
	const printRef = useRef<HTMLDivElement>(null);

	const fetchOrder = useCallback(async () => {
		try {
			setLoading(true);
			const preferredAgencyId = agencyIdFromQuery ? Number(agencyIdFromQuery) : undefined;
			const userAgencyIds = auth.user?.agencies.map((a) => a.agencyId) ?? [];
			const { data } = await loadSaleOrderWithAgencyContext({
				saleOrderId: id || '',
				preferredAgencyId,
				userAgencyIds,
				currentCountry: auth.country,
				dispatch,
			});
			setSalesOrderData(data);
			// Inicializar con todos los pasajeros físicos seleccionados
			const physicalPassengers = data.passengers.filter((p) => p.isPhysicalPerson === true);
			setSelectedPassengerIds(physicalPassengers.map((p) => p.keyId));
		} catch (error) {
			console.error('Error al obtener la orden de venta:', error);
		} finally {
			setLoading(false);
		}
	}, [id, agencyIdFromQuery, auth.user, auth.country, dispatch]);

	useEffect(() => {
		fetchOrder();
	}, [fetchOrder]);

	const handlePrint = useReactToPrint({
		content: () => printRef.current,
		documentTitle: `Resumen_por_Pasajero_Orden_${salesOrderData?.orderNumber || id}`,
	});

	// Función para verificar servicios sin valor en moneda local
	const checkServicesWithoutLocalCurrency = (
		passengerServices: PassengerServicesData[],
		salesOrder: ISaleOrderDetail,
	): string[] => {
		const servicesWithout: string[] = [];

		// Verificar valores del OrderHeader
		if (!(salesOrder as any).saleAmountInLocalCurrency) {
			servicesWithout.push('Monto Total de Venta');
		}
		// Solo verificar chargedAmountInLocalCurrency si chargedAmount > 0
		if (salesOrder.chargedAmount > 0 && !(salesOrder as any).chargedAmountInLocalCurrency) {
			servicesWithout.push('Monto abonado');
		}
		// Solo verificar pendingPayAmountInLocalCurrency si pendingPayAmount > 0
		if (salesOrder.pendingPayAmount > 0 && !(salesOrder as any).pendingPayAmountInLocalCurrency) {
			servicesWithout.push('Monto Pendiente');
		}

		// Verificar servicios de pasajeros
		passengerServices.forEach((passengerData) => {
			passengerData.services.forEach((service) => {
				if (service.saleAmountInLocalCurrency === undefined || service.saleAmountInLocalCurrency === null) {
					const serviceName = `${service.serviceName} - ${service.description}`;
					if (!servicesWithout.includes(serviceName)) {
						servicesWithout.push(serviceName);
					}
				}
			});
		});

		return servicesWithout;
	};

	// Función para manejar el cambio del toggle
	const handleToggleChange = (checked: boolean) => {
		if (checked && salesOrderData) {
			const passengerServices = groupServicesByPassenger(salesOrderData);
			const missingServices = checkServicesWithoutLocalCurrency(passengerServices, salesOrderData);

			if (missingServices.length > 0) {
				setServicesWithoutLocalCurrency(missingServices);
				setWarningDialogOpen(true);
				// No activar el toggle si hay servicios sin valor
				return;
			}
		}

		setUseLocalCurrency(checked);
	};

	if (loading) {
		return (
			<Container maxWidth="lg" sx={{ py: 4, display: 'flex', justifyContent: 'center' }}>
				<CircularProgress />
			</Container>
		);
	}

	if (!salesOrderData) {
		return (
			<Container maxWidth="lg" sx={{ py: 4 }}>
				<Typography variant="h6" color="error">
					No se pudo cargar la información de la orden de venta
				</Typography>
			</Container>
		);
	}

	const passengerServices = groupServicesByPassenger(salesOrderData);
	// Filtrar pasajeros según la selección del multiselect
	const filteredPassengerServices = passengerServices.filter((paxData) =>
		selectedPassengerIds.includes(paxData.passenger.keyId),
	);
	// Obtener todos los pasajeros físicos para el multiselect
	const physicalPassengers = salesOrderData.passengers.filter((p) => p.isPhysicalPerson === true);
	// Obtener el país actual del estado de Redux (basado en la agencia seleccionada)
	const currentCountry = auth.country;
	const effectiveCountry = (countryFromQuery ?? currentCountry) as CountryCodeEnum;
	const isColombia = currentCountry === CountryCodeEnum.COLOMBIA;
	const isParaguay = isParaguayAgency(effectiveCountry);
	const localCurrency = getLocalCurrency(currentCountry);
	const orderVatTotals = isParaguay ? getOrderVatTotals(filteredPassengerServices, effectiveCountry) : null;
	// Solo permitir moneda local si es Colombia
	const effectiveUseLocalCurrency = isColombia && useLocalCurrency;
	const displayCurrency = effectiveUseLocalCurrency ? localCurrency : salesOrderData.currency;

	const handlePassengerSelectionChange = (event: any) => {
		const value = event.target.value;
		setSelectedPassengerIds(typeof value === 'string' ? value.split(',') : value);
	};

	return (
		<Container maxWidth="lg" sx={{ py: 4 }}>
			<Box
				sx={{
					mb: 3,
					'@media print': {
						display: 'none',
					},
				}}
			>
				{/* Primera línea: Título, selector de pasajeros y botón de imprimir */}
				<Box
					sx={{
						display: 'flex',
						justifyContent: 'space-between',
						alignItems: 'center',
						flexWrap: 'wrap',
						gap: 2,
						mb: 2,
					}}
				>
					<Typography variant="h4" component="h1" fontWeight="bold">
						Resumen por Pasajero
					</Typography>
					<Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
						<FormControl sx={{ minWidth: 200, maxWidth: 300 }}>
							<InputLabel id="passenger-select-label">Pasajeros</InputLabel>
							<Select
								labelId="passenger-select-label"
								id="passenger-select"
								multiple
								value={selectedPassengerIds}
								onChange={handlePassengerSelectionChange}
								input={<OutlinedInput label="Pasajeros" />}
								renderValue={(selected) => {
									const selectedArray = selected as string[];
									if (selectedArray.length === 0) {
										return <Typography variant="body2">Seleccionar pasajeros</Typography>;
									}
									const firstPassengerId = selectedArray[0];
									const firstPassenger = physicalPassengers.find((p) => p.keyId === firstPassengerId);
									const remainingCount = selectedArray.length - 1;

									return (
										<Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, overflow: 'hidden' }}>
											<Typography variant="body2" noWrap>
												{firstPassenger
													? `${firstPassenger.name} ${firstPassenger.lastName}`.toUpperCase()
													: firstPassengerId}
											</Typography>
											{remainingCount > 0 && (
												<Chip label={`+${remainingCount}`} size="small" sx={{ height: 20, fontSize: '0.75rem' }} />
											)}
										</Box>
									);
								}}
								MenuProps={{
									PaperProps: {
										style: {
											maxHeight: 300,
										},
									},
								}}
							>
								{physicalPassengers.map((passenger) => (
									<MenuItem key={passenger.keyId} value={passenger.keyId}>
										<Checkbox checked={selectedPassengerIds.indexOf(passenger.keyId) > -1} />
										<ListItemText primary={`${passenger.name} ${passenger.lastName}`.toUpperCase()} />
									</MenuItem>
								))}
							</Select>
						</FormControl>
						<Button
							variant="contained"
							color="primary"
							startIcon={<PictureAsPdfIcon />}
							onClick={handlePrint}
							sx={{ display: { xs: 'none', sm: 'flex' } }}
						>
							Imprimir PDF
						</Button>
					</Box>
				</Box>
				{/* Segunda línea: Todos los toggles */}
				<Box
					sx={{
						display: { xs: 'none', sm: 'flex' },
						alignItems: 'center',
						gap: 2,
						flexWrap: 'wrap',
					}}
				>
					<FormControlLabel
						control={
							<Switch
								checked={showSummaryDetails}
								onChange={(e) => setShowSummaryDetails(e.target.checked)}
								color="primary"
							/>
						}
						label="Mostrar detalle de resumen"
					/>
					<FormControlLabel
						control={
							<Switch
								checked={showReferencesAndTickets}
								onChange={(e) => setShowReferencesAndTickets(e.target.checked)}
								color="primary"
							/>
						}
						label="Mostrar localizadores y tickets"
					/>
					<FormControlLabel
						control={
							<Switch
								checked={showCo2Emission}
								onChange={(e) => setShowCo2Emission(e.target.checked)}
								color="primary"
							/>
						}
						label="Mostrar Emisión de CO₂"
					/>
					{isColombia && (
						<FormControlLabel
							control={
								<Switch
									checked={useLocalCurrency}
									onChange={(e) => handleToggleChange(e.target.checked)}
									color="primary"
								/>
							}
							label="Precios en moneda local"
						/>
					)}
				</Box>
			</Box>

			<Box
				ref={printRef}
				sx={{
					'@media print': {
						'& button': {
							display: 'none',
						},
					},
				}}
			>
				<Paper elevation={3} sx={{ p: 3, mb: 3 }}>
					<OrderHeader
						salesOrder={salesOrderData}
						useLocalCurrency={effectiveUseLocalCurrency}
						localCurrency={localCurrency}
						showSummaryDetails={showSummaryDetails}
						vatTotals={orderVatTotals ?? undefined}
					/>
				</Paper>

				<Grid container spacing={3}>
					{filteredPassengerServices.map((passengerData) => (
						<Grid item xs={12} key={passengerData.passenger.keyId}>
							<PassengerSummary
								passengerData={passengerData}
								currency={displayCurrency}
								travelDepartureDate={salesOrderData.travelDepartureDate}
								useLocalCurrency={effectiveUseLocalCurrency}
								showReferencesAndTickets={showReferencesAndTickets}
								showCo2Emission={showCo2Emission}
								country={effectiveCountry}
							/>
						</Grid>
					))}
				</Grid>
			</Box>

			<Box sx={{ mt: 3, display: { xs: 'flex', sm: 'none' }, flexDirection: 'column', gap: 2, alignItems: 'center' }}>
				<FormControlLabel
					control={
						<Switch
							checked={showSummaryDetails}
							onChange={(e) => setShowSummaryDetails(e.target.checked)}
							color="primary"
						/>
					}
					label="Mostrar detalle de resumen"
				/>
				<FormControlLabel
					control={
						<Switch
							checked={showReferencesAndTickets}
							onChange={(e) => setShowReferencesAndTickets(e.target.checked)}
							color="primary"
						/>
					}
					label="Mostrar localizadores y tickets"
				/>
				<FormControlLabel
					control={
						<Switch checked={showCo2Emission} onChange={(e) => setShowCo2Emission(e.target.checked)} color="primary" />
					}
					label="Mostrar Emisión de CO₂"
				/>
				{isColombia && (
					<FormControlLabel
						control={
							<Switch
								checked={useLocalCurrency}
								onChange={(e) => handleToggleChange(e.target.checked)}
								color="primary"
							/>
						}
						label="Precios en moneda local"
					/>
				)}
				<Button variant="contained" color="primary" startIcon={<PictureAsPdfIcon />} onClick={handlePrint} fullWidth>
					Imprimir PDF
				</Button>
			</Box>

			{/* Dialog de advertencia para servicios sin valor en moneda local */}
			<Dialog open={warningDialogOpen} onClose={() => setWarningDialogOpen(false)} maxWidth="sm" fullWidth>
				<DialogTitle>
					<Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
						<WarningIcon color="warning" />
						<Typography variant="h6" component="span">
							Servicios sin valor en moneda local
						</Typography>
					</Box>
				</DialogTitle>
				<DialogContent>
					<Typography variant="body1" sx={{ mb: 2 }}>
						Los siguientes servicios no tienen valor de venta en moneda local (COP) configurado. Debe configurar estos
						valores antes de poder ver la vista en este formato:
					</Typography>
					<List dense>
						{servicesWithoutLocalCurrency.map((serviceName, index) => (
							<ListItem key={index} sx={{ py: 0.5 }}>
								<ListItemText primary={serviceName} />
							</ListItem>
						))}
					</List>
				</DialogContent>
				<DialogActions>
					<Button onClick={() => setWarningDialogOpen(false)} color="primary" variant="contained">
						Entendido
					</Button>
				</DialogActions>
			</Dialog>
		</Container>
	);
};
